import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { api } from '../utils/api';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming', 'District of Columbia'
];

const AddDBADialog = ({ open, onClose, onSuccess }) => {
  const [entities, setEntities] = useState([]);
  const [formData, setFormData] = useState({
    entity_id: '',
    dba_name: '',
    state: '',
    county: '',
    registration_date: '',
    expiration_date: '',
    owner: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadEntities();
      // Reset form when dialog opens
      setFormData({
        entity_id: '',
        dba_name: '',
        state: '',
        county: '',
        registration_date: '',
        expiration_date: '',
        owner: '',
        status: 'active'
      });
    }
  }, [open]);

  const loadEntities = async () => {
    try {
      const data = await api.getEntities();
      setEntities(data.entities || []);
    } catch (error) {
      console.error('Failed to load entities:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dbaData = {
        entity_id: formData.entity_id,
        dba_name: formData.dba_name,
        state: formData.state,
        county: formData.county,
        owner: formData.owner,
        status: formData.status
      };

      if (formData.registration_date) {
        dbaData.registration_date = new Date(formData.registration_date).toISOString();
      }
      if (formData.expiration_date) {
        dbaData.expiration_date = new Date(formData.expiration_date).toISOString();
      }

      // Call API to create DBA
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dbas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dbaData)
      });

      if (!response.ok) {
        throw new Error('Failed to create DBA');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create DBA:', error);
      alert('Failed to create DBA. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="add-dba-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Add New DBA</DialogTitle>
          <DialogDescription>
            Register a new Doing Business As (DBA) name for your entity
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Entity Selection */}
            <div className="space-y-2">
              <Label htmlFor="entity_id">Associated Entity *</Label>
              <Select value={formData.entity_id} onValueChange={(value) => setFormData({ ...formData, entity_id: value })} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select entity..." />
                </SelectTrigger>
                <SelectContent>
                  {entities.map(entity => (
                    <SelectItem key={entity.entity_id} value={entity.entity_id}>
                      {entity.legal_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* DBA Name */}
            <div className="space-y-2">
              <Label htmlFor="dba_name">DBA Name *</Label>
              <Input
                id="dba_name"
                value={formData.dba_name}
                onChange={(e) => setFormData({ ...formData, dba_name: e.target.value })}
                placeholder="Enter DBA name"
                required
              />
            </div>

            {/* State and County */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select state..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="county">County</Label>
                <Input
                  id="county"
                  value={formData.county}
                  onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                  placeholder="Enter county name"
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="registration_date">Registration Date</Label>
                <Input
                  id="registration_date"
                  type="date"
                  value={formData.registration_date}
                  onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiration_date">Expiration Date</Label>
                <Input
                  id="expiration_date"
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                />
              </div>
            </div>

            {/* Owner and Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="owner">Owner Email</Label>
                <Input
                  id="owner"
                  type="email"
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                  placeholder="owner@losspay.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-muted/30 p-4 rounded-lg border border-border mt-4">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> Ensure the DBA name is registered with the appropriate state and county authorities before adding it here.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create DBA'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddDBADialog;
